import axios, { isAxiosError } from 'axios';
import { API_URL } from '@/configs';
import i18n from '@/i18n';
import { getDeviceFingerprint } from '@/lib/fingerprint';
import { camelizeKeys, decamelizeKeys } from '@/lib/utils';
import { announceLocked, getUnlockToken } from '@/pages/safezone/pinLock';

const http = axios.create({ baseURL: API_URL });

const setHTTPHeader = (headers: Record<string, string>) => {
  http.defaults.headers.common = { ...http.defaults.headers.common, ...headers };
};

const initHTTPHeader = () => {
  const token = localStorage.getItem('token');
  if (token) setHTTPHeader({ Authorization: token });
};

http.interceptors.request.use((config) => {
  if (config.data && typeof config.data === 'object' && !(config.data instanceof FormData)) {
    config.data = decamelizeKeys(config.data);
  }
  // Bind every request to this browser's device fingerprint so stolen tokens can't be
  // replayed from a different browser / machine.
  config.headers['X-Device-Fingerprint'] = getDeviceFingerprint();
  // The language the person chose in the app, not the browser's. Before sign-in
  // the server has no users.lang to go on, so this is what puts a failed login
  // or an expired session in French for someone who picked French on an
  // English browser (the same seam the phone missed — docs/INSTRUMENTS.md §7).
  config.headers['Accept-Language'] = i18n.language || 'en';
  // The token is normally a default header, set at boot and on login. Reading
  // it here as well closes a race: a query that fires the moment a component
  // mounts could go out before that default was applied, come back 401, and
  // the interceptor below would sign the user out.
  if (!config.headers.Authorization) {
    const token = localStorage.getItem('token');
    if (token) config.headers.Authorization = token;
  }
  // SafeZone PIN: attach the short-lived unlock token to SafeZone API calls.
  if (config.url?.includes('/safezone_app/')) {
    const unlock = getUnlockToken();
    if (unlock) config.headers['X-Safezone-Unlock'] = unlock;
  }
  return config;
});

http.interceptors.response.use(
  (response) => {
    // Never transform binary payloads (file downloads) — camelizing a Blob or
    // ArrayBuffer would destroy it.
    if (response.data instanceof Blob || response.data instanceof ArrayBuffer) {
      return response;
    }
    if (response.data && typeof response.data === 'object') {
      response.data = camelizeKeys(response.data);
    }
    return response;
  },
  async (error: unknown) => {
    if (isAxiosError(error)) {
      // SafeZone gate: the server says the unlock expired/changed — relock the UI.
      if (
        error.response?.status === 403 &&
        (error.response.data as { error?: string } | undefined)?.error === 'safezone_pin_locked'
      ) {
        announceLocked();
        return Promise.reject(error.response.data);
      }
      if (error.response?.status === 401) {
        // Avoid redirect loop if already on auth pages
        if (!window.location.pathname.startsWith('/login')) {
          localStorage.removeItem('user');
          localStorage.removeItem('token');
          delete http.defaults.headers.common['Authorization'];
          const intendedPath = window.location.pathname + window.location.search;
          if (intendedPath !== '/') {
            sessionStorage.setItem('redirectAfterLogin', intendedPath);
          }
          window.location.href = '/login';
        }
        return error;
      }
      return Promise.reject(error.response?.data ?? 'Unknown Error');
    }
    return Promise.reject('Unknown Error');
  },
);

export { http, setHTTPHeader, initHTTPHeader };
